// AWS's rule for a secret's name. It admits no whitespace, colon or wildcard,
// so neither a pasted key, an ARN nor a pattern matches.
const AWS_SECRET_NAME = /^[A-Za-z0-9/_+=.@-]{1,512}$/;

// The hyphen and six letters or digits that Secrets Manager adds to a secret's
// name in its full ARN. It reads a partial ARN that ends this way as a full
// one, and finds no secret by it.
const AWS_ARN_SUFFIX = /-[A-Za-z0-9]{6}$/;

/** Refuses anything that isn't a secret's name, such as its ARN or a pasted
 * key, without repeating it. */
export const INVALID_SECRET_NAME =
  "That doesn't look like a secret name. Enter the name of the secret that holds the key, not its ARN or the key itself.";

/** Refuses a name that Secrets Manager would read as ending in a full ARN's
 * suffix. */
export const SUFFIXED_SECRET_NAME =
  "Secrets Manager can't find a secret by its name when the name ends in a hyphen and six characters, like -AbCdEf. Use a secret whose name doesn't end that way.";

/**
 * Why `name`, ignoring surrounding whitespace, can't name the private key's
 * secret, or `undefined` if it can.
 *
 * P0 points the connector at the secret by its partial ARN (see
 * {@link toSecretPartialArn}), so the name must be one that Secrets Manager
 * finds by it. Each problem is a fixed message: none repeats `name`, which may
 * be a key pasted in its place.
 */
export const findSecretNameProblem = (name: unknown): string | undefined => {
  const candidate = typeof name === "string" ? name.trim() : undefined;
  if (candidate === undefined || !AWS_SECRET_NAME.test(candidate)) {
    return INVALID_SECRET_NAME;
  }
  return AWS_ARN_SUFFIX.test(candidate) ? SUFFIXED_SECRET_NAME : undefined;
};

/**
 * The partial ARN of the AWS Secrets Manager secret `name`, in `accountId` and
 * `region`: the secret's ARN without the hyphen and six random characters that
 * Secrets Manager adds to its name.
 *
 * P0 points the connector at the private key's secret by it, since Secrets
 * Manager finds a secret by its partial ARN from another account too. The
 * connector's Terraform grants read on it followed by `-??????`.
 *
 * Each value is trimmed, as P0 stores it. P0 takes only regions in AWS's
 * commercial partition, so the partition is `aws`.
 */
export const toSecretPartialArn = ({
  accountId,
  name,
  region,
}: {
  accountId: string;
  name: string;
  region: string;
}) =>
  `arn:aws:secretsmanager:${region.trim()}:${accountId.trim()}:secret:${name.trim()}`;
